import { useEffect, useState, useMemo } from 'react';
import './CalendarSettings.css';

import OptionsDialog from '../OptionsDialog/OptionsDialog.jsx';
import EditUserCalenderRights from '../EditUserCalenderRights/EditUserCalenderRights.jsx';

const BUILTIN_COLUMNS = [
  { kind: 'builtin', key: 'DATE', title: 'Datum', merge: false, export: true },
  { kind: 'builtin', key: 'SUMMARY', title: 'Titel', merge: false, export: true },
  { kind: 'builtin', key: 'DESCRIPTION', title: 'Beschreibung', merge: false, export: true },
  { kind: 'builtin', key: 'LOCATION', title: 'Ort', merge: false, export: true },
];

const DEFAULT_CONFIG = { columns: [...BUILTIN_COLUMNS] };

const normalizeColor = (c) => {
  if (!c) return '';
  let x = String(c).trim();
  if (!x.startsWith('#')) x = '#' + x;
  return /^#[0-9a-fA-F]{6}$/.test(x) ? x.toUpperCase() : '';
};


export default function CalendarSettings({ calendarid, onClose, onSaved, currentUsername }) {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(DEFAULT_CONFIG);
  const [exportSettings, setExportSettings] = useState({});
  const [error, setError] = useState(null);
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const [optionsIdx, setOptionsIdx] = useState(null);
  const [err, setErr] = useState(null);

  const [showEditRights, setShowEditRights] = useState(false);

  // Logo preview
  const logoUrl = exportSettings.Logo ? `/logos/${exportSettings.Logo}` : null;

  const loadConfig = () => {
    setLoading(true);
    setError(null);
    fetch(`/get_calendar_config.php?id=${encodeURIComponent(calendarid)}`)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(j => {
        if (!j.ok) throw new Error(j.error || 'Load failed');

        const content = j.content ?? j; // kompatibel zu alten/neuen Antworten
        const incomingCfg = (content.config && Array.isArray(content.config.columns))
          ? content.config
          : DEFAULT_CONFIG;

        const cols = (incomingCfg.columns || DEFAULT_CONFIG.columns).map(c => ({ export: true, ...c }));
        setCfg({ ...incomingCfg, columns: cols });

        const es = content.exportSettings || {};
        setExportSettings(es);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (calendarid) loadConfig(); }, [calendarid]);


  const moveRow = (idx, dir) => {
    setCfg(prev => {
      const cols = [...prev.columns];
      const j = idx + dir;
      if (j < 0 || j >= cols.length) return prev;
      [cols[idx], cols[j]] = [cols[j], cols[idx]];
      return { ...prev, columns: cols };
    });
  };

  const addField = () => {
    setCfg(prev => ({
      ...prev,
      columns: [
        ...prev.columns,
        {
          kind: 'xkal',
          var: 'NEW-VAR',
          label: 'Neues Feld',
          type: 'text',
          weight: 1.0,
          required: false,
          export: true,
          options: [],
          align: 'left',
        }
      ]
    }));
  };

  // Universelles Patchen (auch Builtins); Normalisierung nur für xkal
  const updateField = (idx, patch) => {
    setCfg(prev => {
      const cols = [...prev.columns];
      const col = cols[idx];
      if (!col) return prev;

      const next = { ...col, ...patch };

      if (col.kind === 'xkal') {
        if (patch.var !== undefined) {
          next.var = String(patch.var).replace(/[^A-Za-z0-9-]/g, '').toUpperCase();
        }
        if (patch.options !== undefined && !Array.isArray(patch.options)) {
          next.options = [];
        }
        if (patch.align !== undefined) {
          const a = String(patch.align).toLowerCase();
          next.align = ['left', 'center', 'right', 'block'].includes(a) ? a : 'left';
        }
      }

      cols[idx] = next;
      return { ...prev, columns: cols };
    });
  };

  const removeField = (idx) => {
    setCfg(prev => {
      const cols = [...prev.columns];
      if (cols[idx]?.kind === 'builtin') return prev; // builtins nicht löschen
      cols.splice(idx, 1);
      return { ...prev, columns: cols };
    });
  };

  const save = () => {
    // Packe ExportSettings + Config zusammen wie bisher
    fetch('/save_calendar_config.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exportSettings, config: cfg })
    })
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(j => {
        if (!j.ok) throw new Error(j.error || 'Save failed');
        console.log('Einstellungen gespeichert.', cfg);
        onSaved && onSaved(cfg);
        onClose && onClose(calendarid, exportSettings.Name);
      })
      .catch(e => setError(e.message));
  };


  const handleLogoSelect = (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);           // keep this name in sync with PHP
    form.append('calendarId', calendarid || '');

    fetch('/upload_logo.php', { method: 'POST', body: form })
      .then(async (r) => {
        const text = await r.text();     // read once
        let json = {};
        try { json = JSON.parse(text); } catch (_) { console.warn('Upload response is not JSON: ' + text); }
        if (!r.ok || json.ok === false) {
          throw new Error(json.error || `HTTP ${r.status} ${r.statusText} — ${text}`);
        }
        return json;
      })
      .then(j => {
        const fname = j.filename || j.file || j.name;
        if (!fname) throw new Error('Upload response missing filename.');
        setExportSettings(prev => ({ ...prev, Logo: fname }));
      })
      .catch(e => console.error('Logo-Upload fehlgeschlagen: ' + (e.message || e)));
  };



  if (!calendarid) return null;

  return (
    <div className="settings-backdrop" onClick={() => onClose()}>
      <div className="settings-dialog" onClick={e => e.stopPropagation()}>
        <div className="settings-body">
          <div className="settings-header">
            <h3>Kalender-Einstellungen</h3>
          </div>

          {loading ? (
            <p>Lade…</p>
          ) : (
            <>
              {error && <div className="settings-error">{error}</div>}

              <div className="settings-general">
                <h4>Allgemein</h4>

                <div className="field">
                  <label htmlFor="calendar-name">Name:</label>
                  <input
                    id="calendar-name"
                    type="text"
                    value={exportSettings.Name || ''}
                    onChange={e => setExportSettings(prev => ({ ...prev, Name: e.target.value }))}
                  />
                </div>




                <div className="field">
                  <label>Logo:</label>
                  <div className="logo-row">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoSelect(e.target.files?.[0])}
                    />
                    {logoUrl && (
                      <img
                        className="logo-preview"
                        src={logoUrl}
                        alt="Logo"
                      />
                    )}
                  </div>
                </div>


                <div className="field">
                  <label htmlFor="calendar-header-size">Überschriftgröße:</label>
                  <input
                    id="calendar-header-size"
                    type="number"
                    value={exportSettings.HeaderSize || 16}
                    onChange={e => setExportSettings(prev => ({ ...prev, HeaderSize: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label htmlFor="calendar-font-size">Schriftgröße:</label>
                  <input
                    id="calendar-font-size"
                    type="number"
                    value={exportSettings.FontSize || 12}
                    onChange={e => setExportSettings(prev => ({ ...prev, FontSize: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label>Überschrift-Hintergrund:</label>
                  <input
                    type="color"
                    value={normalizeColor(exportSettings.HeaderBackgroundColor) || '#FFFFFF'}
                    onChange={e => setExportSettings(prev => ({ ...prev, HeaderBackgroundColor: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label>Überschrift-Schriftfarbe:</label>
                  <input
                    type="color"
                    value={normalizeColor(exportSettings.HeaderFontColor) || '#000000'}
                    onChange={e => setExportSettings(prev => ({ ...prev, HeaderFontColor: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label>Standart-Startzeit:</label>
                  <input type='checkbox'
                    checked={exportSettings.DefaultStartTimeEnabled}
                    onChange={e => setExportSettings(prev => ({ ...prev, DefaultStartTimeEnabled: e.target.checked }))}
                  />
                  <input
                    type="time"
                    value={exportSettings.DefaultStartTime || '08:00'}
                    onChange={e => setExportSettings(prev => ({ ...prev, DefaultStartTime: e.target.value }))}
                  />
                </div>

                <div className="field">
                  <label htmlFor="calendar-footer">Footertext:</label>
                  <textarea
                    id="calendar-footer"
                    value={exportSettings.FooterText || ''}
                    onChange={e => setExportSettings(prev => ({ ...prev, FooterText: e.target.value }))}
                  />
                </div>


                <button className="primary" onClick={() => setShowEditRights(true)}>Rechte bearbeiten</button>



              </div>

              <h4>Datenfelder</h4>
              <div className="fields-list">
                <div className="list-head">
                  <span className="col-sort">Sort</span>
                  <span className="col-kind">Typ</span>
                  <span className="col-var">Spalte / X-KAL-Variable</span>
                  <span className="col-type">Eingabetyp</span>
                  <span className="col-opts">Optionen</span>
                  <span className="col-req">Pflicht</span>
                  <span className="col-weight">Gewicht</span>
                  <span className="col-align">Ausrichtung</span>
                  <span className="col-actions">Löschen</span>
                  <span className="col-merge">Reihen zusammenführen</span>
                  <span className="col-export">Export</span>
                </div>

                {cfg.columns.map((c, idx) => {
                  const isBuiltin = c.kind === 'builtin';
                  const type = isBuiltin ? 'builtin' : (c.type || 'text');

                  return (
                    <div key={idx} className="list-row">
                      <div className="col-sort">
                        <button onClick={() => moveRow(idx, -1)} disabled={idx === 0}>▲</button>
                        <button onClick={() => moveRow(idx, 1)} disabled={idx === cfg.columns.length - 1}>▼</button>
                      </div>

                      <div className="col-kind">{isBuiltin ? 'Builtin' : 'X-KAL'}</div>

                      <input
                        className="col-var"
                        value={isBuiltin ? (c.title || c.key) : (c.var || '')}
                        onChange={e => !isBuiltin && updateField(idx, { var: e.target.value })}
                        disabled={isBuiltin}
                      />

                      <select
                        className="col-type"
                        value={type}
                        onChange={e => !isBuiltin && updateField(idx, { type: e.target.value })}
                        disabled={isBuiltin}
                      >
                        {isBuiltin ? <option value="builtin">—</option> : (
                          <>
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="time">Time</option>
                            <option value="select">Select</option>
                          </>
                        )}
                      </select>

                      <div className="col-opts">
                        {
                          (!isBuiltin && (c.type || 'text') === 'select')
                            ? (
                              <button
                                type="button"
                                onClick={() => { setOptionsIdx(idx); setShowOptionsDialog(true); }}
                              >
                                Optionen
                              </button>
                            )
                            : <button disabled type="button">Optionen</button>
                        }
                      </div>


                      <input
                        type="checkbox"
                        className="col-req"
                        checked={!!c.required}
                        onChange={e => updateField(idx, { required: e.target.checked })}
                        disabled={isBuiltin}
                      />

                      <input
                        type="number"
                        className="col-weight"
                        value={c.weight || 1.0}
                        step="0.1" min="0" max="10"
                        onChange={e => updateField(idx, { weight: parseFloat(e.target.value || '0') })}
                        disabled={false}
                      />

                      <div className="col-align">
                        <select
                          value={c.align || 'left'}
                          onChange={e => updateField(idx, { align: e.target.value })}
                        >
                          <option value="left">Links</option>
                          <option value="center">Mitte</option>
                          <option value="right">Rechts</option>
                          <option value="block">Block</option>
                        </select>
                      </div>

                      <div className="col-actions">
                        <button onClick={() => removeField(idx)} disabled={isBuiltin}>❌</button>
                      </div>

                      <input
                        className="col-merge"
                        type="checkbox"
                        checked={c.merge !== false}
                        onChange={e => updateField(idx, { merge: e.target.checked })}
                      />

                      <input
                        className="col-export"
                        type="checkbox"
                        checked={c.export !== false}
                        onChange={e => updateField(idx, { export: e.target.checked })}
                      />
                    </div>
                  );
                })}

                <button className="add-btn" onClick={addField}>+ X-KAL-Feld hinzufügen</button>
              </div>

              <div className="settings-footer">
                <button onClick={() => onClose()}>Abbrechen</button>
                <button className="primary" onClick={save}>Speichern</button>
              </div>

              <OptionsDialog
                open={showOptionsDialog}
                value={optionsIdx != null ? (cfg.columns[optionsIdx]?.options || []) : []}
                onClose={() => { setShowOptionsDialog(false); setOptionsIdx(null); }}
                onSave={(opts) => {
                  // Optionen in die Spalte zurückschreiben und Dialog schließen
                  if (optionsIdx != null) {
                    // deine vorhandene updateField-Funktion nutzen
                    updateField(optionsIdx, { options: opts });
                  }
                  setShowOptionsDialog(false);
                  setOptionsIdx(null);
                }}
              />

              {showEditRights && (
                <EditUserCalenderRights
                  calendarid={calendarid}
                  currentUsername={currentUsername}     // <-- weiterreichen
                  onSaved={() => {                      // <-- App informieren
                    onSaved?.();
                    setShowEditRights(false);
                  }}
                // onClose={() => {
                //   onClose?.(calendarid);              // oder nur schließen, je nach Logik
                // }}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
